import { LEARNING_HUB_ROUTE } from 'config'

/**
 * Ordered flat list of { chapterId, block } for Next / tracker navigation.
 * @param {Array<{ id: number, blocks?: Array }>} chapters
 * @returns {Array<{ chapterId: number, block: object }>}
 */
export function flattenCourseBlocks(chapters) {
  if (!Array.isArray(chapters)) return []

  const sortedChapters = [...chapters].sort(
    (a, b) => Number(a.id) - Number(b.id)
  )

  return sortedChapters.flatMap((chapter) => {
    const blocks = [...(chapter.blocks || [])].sort((a, b) => {
      const oa = Number(a.order) || 0
      const ob = Number(b.order) || 0
      if (oa !== ob) return oa - ob
      return Number(a.id) - Number(b.id)
    })
    return blocks.map((block) => ({
      chapterId: chapter.id,
      block,
    }))
  })
}

/**
 * @param {object} course — outline from fetchLearningHubCourse
 * @param {string|number} currentBlockId
 * @returns {{ chapterId: number, block: object } | null}
 */
export function getNextBlockEntry(course, currentBlockId) {
  const flat = flattenCourseBlocks(course?.chapters)
  const idx = flat.findIndex(
    (entry) => Number(entry.block.id) === Number(currentBlockId)
  )
  if (idx < 0 || idx >= flat.length - 1) return null
  return flat[idx + 1]
}

export function blockAttemptStatus(block) {
  return block?.attemptStatus ?? block?.attempt_status
}

export function isBlockCompleted(block) {
  return blockAttemptStatus(block) === 'completed'
}

/**
 * Learner may open a block only after every prior block in course order is completed.
 * @param {object} course — outline from fetchLearningHubCourse
 * @param {string|number} blockId
 * @returns {boolean}
 */
export function isBlockUnlockedInCourse(course, blockId) {
  const flat = flattenCourseBlocks(course?.chapters)
  const idx = flat.findIndex(
    (entry) => Number(entry.block.id) === Number(blockId)
  )
  if (idx < 0) return false
  if (idx <= 0) return true
  return isBlockCompleted(flat[idx - 1].block)
}

/**
 * Whether block id exists under chapter in course outline.
 */
export function blockBelongsToCourseChapter(course, chapterId, blockId) {
  const chapter = (course?.chapters || []).find(
    (ch) => Number(ch.id) === Number(chapterId)
  )
  if (!chapter) return false
  return (chapter.blocks || []).some((b) => Number(b.id) === Number(blockId))
}

export function isChapterCompleted(chapter) {
  const blocks = chapter?.blocks || []
  if (!blocks.length) return true
  return blocks.every((block) => isBlockCompleted(block))
}

export function isCourseCompleted(course) {
  const flat = flattenCourseBlocks(course?.chapters)
  if (!flat.length) return false
  return flat.every(({ block }) => isBlockCompleted(block))
}

export function courseCompletionPath(courseId) {
  return `${LEARNING_HUB_ROUTE}/course/${courseId}/certificate`
}
